import * as stylex from '@stylexjs/stylex'
import { useState, type ChangeEvent } from 'react'

import Button from './Button'
import Card from './Card'
import Icon from './Icon'
import type { ImportPreview } from '../lib/ledger'

const styles = stylex.create({
  content: { display: 'flex', flexDirection: 'column', rowGap: '18px' },
  heading: { display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '16px' },
  title: { fontSize: '18px', fontWeight: 600 },
  copy: { marginTop: '6px', color: '#748077', fontSize: '13px', lineHeight: '1.35rem' },
  icon: { display: 'flex', height: '40px', width: '40px', flexShrink: 0, alignItems: 'center', justifyContent: 'center', borderRadius: '0.75rem', backgroundColor: '#edf6eb', color: '#468554' },
  fileLabel: { display: 'flex', minHeight: '48px', cursor: 'pointer', alignItems: 'center', justifyContent: 'center', gap: '8px', borderWidth: 1, borderStyle: 'dashed', borderColor: '#bfd2bd', borderRadius: '0.75rem', backgroundColor: '#f8fbf7', paddingInline: '16px', color: '#447a50', fontSize: '13px', fontWeight: 600, ':hover': { backgroundColor: '#f0f7ee' } },
  hiddenInput: { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  preview: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' },
  metric: { borderRadius: '0.75rem', backgroundColor: '#f5f8f4', padding: '12px', textAlign: 'center' },
  metricValue: { fontSize: '18px', fontWeight: 650, color: '#355c3e' },
  metricLabel: { marginTop: '2px', color: '#748077', fontSize: '11px' },
  message: { borderRadius: '0.75rem', padding: '10px 12px', fontSize: '12px' },
  error: { backgroundColor: '#fff3ed', color: '#9b491e' },
  success: { backgroundColor: '#edf7eb', color: '#397a4b' },
  actions: { display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '8px', borderTopWidth: 1, borderColor: '#edf0eb', paddingTop: '14px' },
})

type Props = {
  onPreview: (file: File) => Promise<ImportPreview>
  onCommit: (importID: string) => Promise<void>
  onDiscard: (importID: string) => Promise<void>
}

export default function SplitwiseImportCard({ onPreview, onCommit, onDiscard }: Props) {
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [fileName, setFileName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  async function selectFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setBusy(true)
    setError('')
    setDone(false)
    try {
      const result = await onPreview(file)
      setPreview(result)
      setFileName(file.name)
    } catch (reason) {
      setPreview(null)
      setFileName('')
      setError(reason instanceof Error ? reason.message : 'Could not preview the Splitwise file.')
    } finally {
      setBusy(false)
    }
  }

  async function commit() {
    if (!preview || busy) return
    setBusy(true)
    setError('')
    try {
      await onCommit(preview.id)
      setPreview(null)
      setFileName('')
      setDone(true)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not import the Splitwise data.')
    } finally {
      setBusy(false)
    }
  }

  async function discard() {
    if (!preview || busy) return
    setBusy(true)
    setError('')
    try {
      await onDiscard(preview.id)
      setPreview(null)
      setFileName('')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not discard the import preview.')
    } finally {
      setBusy(false)
    }
  }

  return <Card aria-labelledby="splitwise-import-heading" padding="lg"><div {...stylex.props(styles.content)}>
    <div {...stylex.props(styles.heading)}>
      <div><h2 id="splitwise-import-heading" {...stylex.props(styles.title)}>Import from Splitwise</h2><p {...stylex.props(styles.copy)}>Choose your Splitwise CSV export. SettleUp previews expenses, payments, and loans before anything is added.</p></div>
      <span {...stylex.props(styles.icon)}><Icon name="sync" /></span>
    </div>

    {!preview && <label {...stylex.props(styles.fileLabel)}><Icon name="plus" />{busy ? 'Reading file…' : 'Choose Splitwise CSV'}<input accept=".csv,text/csv" {...stylex.props(styles.hiddenInput)} disabled={busy} onChange={selectFile} type="file" /></label>}

    {preview && <>
      <p {...stylex.props(styles.copy)}><strong>{fileName}</strong> is ready to import.</p>
      <div {...stylex.props(styles.preview)}>
        <div {...stylex.props(styles.metric)}><p {...stylex.props(styles.metricValue)}>{preview.expenses?.length ?? 0}</p><p {...stylex.props(styles.metricLabel)}>Expenses</p></div>
        <div {...stylex.props(styles.metric)}><p {...stylex.props(styles.metricValue)}>{preview.settlements?.length ?? 0}</p><p {...stylex.props(styles.metricLabel)}>Payments</p></div>
        <div {...stylex.props(styles.metric)}><p {...stylex.props(styles.metricValue)}>{preview.loans?.length ?? 0}</p><p {...stylex.props(styles.metricLabel)}>Loans</p></div>
      </div>
      <div {...stylex.props(styles.actions)}><Button disabled={busy} onClick={() => void discard()} variant="secondary">Cancel</Button><Button disabled={busy} onClick={() => void commit()}>{busy ? 'Importing…' : 'Import records'}</Button></div>
    </>}

    {error && <p {...stylex.props(styles.message, styles.error)} role="alert">{error}</p>}
    {done && <p {...stylex.props(styles.message, styles.success)} role="status">Splitwise data imported successfully.</p>}
  </div></Card>
}
